import { ApiEndpoint } from './ApiEndpoint.js';
import { Env } from './Env.js';

/**
 * Shared class for browser requests to the API.
 *
 * Use only when the result doesn't need to be indexable by search engines — form
 * submissions, post-load pagination, per-visitor hydration on cached pages. Most
 * user-facing content must be server-side rendered so search engines can index it;
 * fetch that with APIRequestV2.php in the page controller instead (the generally
 * preferred path). Browser requests carry no secret, so only public endpoints work.
 */
export class APIRequest {
    path;
    params;

    constructor(path, params) {
        this.path = path;
        this.params = params;
    }

    /**
     * Performs API request.
     * @returns {Promise<Object>} The JSON response from the API.
     * @throws {Error} Throws an error if the network request fails or the response is invalid.
     */
    async fetch() {
        let response;
        try {
            // URLSearchParams converts null to the string "null", which breaks PHP's empty() checks.
            const cleanParams = Object.fromEntries(
                Object.entries(this.params || {}).filter(([_, v]) => v != null)
            );

            response = await fetch(this._getApiUrl() + this.path, {
                method: "POST",
                body: new URLSearchParams(cleanParams),
            });
        } catch (exception) {
            // Make fetch error message more user-friendly.
            console.log("Fetch error:", exception);
            if (exception instanceof TypeError) {
                throw new Error(`You're offline. Please try again.`);
            }
        }

        // Parse response.
        const result = await this._didReceiveResponse(response);
        return result;
    }

    /**
     * Processes the API response.
     * @param {Response} response The response object from the fetch request.
     * @returns {Promise<Object>} The parsed JSON object from the response.
     * @throws {Error} Throws an error if the response cannot be parsed or contains an error.
     */
    async _didReceiveResponse(response) {
        if (!response) {
            throw new Error("No response from server.");
        }

        // Parse response as JSON.
        const text = await response.text(); // Consumes response body.
        let json;
        try {
            json = JSON.parse(text);
        } catch (error) {
            throw new Error(`Parsing error: ${error}\n${text}`);
        }

        // Check for structured error.
        if (json?.success !== true) {
            const { error } = json;
            const title = error?.title ?? "Unknown Error";
            const reason = error?.reason ?? "Please try again or contact support";
            const exception = new Error(`${title}\n${reason}`);
            exception.tag = error?.tag;
            exception.code = error?.code;
            throw exception;
        }

        return json;
    }

    /**
     * Helper method to convert an array of users to an object keyed by uid.
     * @param {Array} userArray The array of user objects.
     * @returns {Object} An object where each key is a user's uid and the value is the user object.
     */
    static usersByUid(userArray) {
        const usersByUid = {};
        if (!Array.isArray(userArray)) {
            console.error('Invalid input: userArray is not an array');
            return usersByUid;
        }

        // Check if user is an object and has a uid,
        for (const user of userArray) {
            if (user && typeof user === 'object' && user.uid) {
                usersByUid[user.uid] = user;
            }
        }

        return usersByUid;
    }

    /**
     * Gets the base URL for the API. Deployed pages always target prod; in dev (see
     * Env.isDevEnv) the per-browser /dev page selection decides, defaulting to staging.
     * @returns {string} The base URL for the API.
     */
    _getApiUrl() {
        if (Env.isDevEnv()) {
            return ApiEndpoint.url(ApiEndpoint.current());
        }
        return ApiEndpoint.url(ApiEndpoint.Prod);
    }
}
